/**
 * K0.6 / Befund F4: Was der Komponist beantwortet und gefragt hat, war unsichtbar.
 *
 * `POST /api/inquiries/:id/komponieren` legt die Antworten auf die Kundenfragen und die eigenen
 * Rueckfragen in `events.details` ab - angezeigt wurden sie nirgends. Damit blieben genau die
 * zwei Benchmark-Kriterien unsichtbar, an denen sich der Komponist messen lassen muss:
 * B4 („jede Kundenfrage beantwortet") und B7 („richtige Rueckfragen"). Antoine suchte am
 * 02.08.2026 bereits vergeblich nach dem Anschreiben.
 *
 * `details` ist `unknown` (jsonb aus der DB, ohne Server-Schema). Deshalb wird hier nichts
 * geglaubt, sondern geprueft: Was nicht die erwartete Form hat, faellt weg. Eine halb geratene
 * Anzeige waere schlimmer als keine.
 *
 * Rein und ohne React - damit lesbar und ohne Oberflaeche nachvollziehbar.
 */

export interface KompositionsAntwort {
  frage: string;
  antwort: string;
}

/**
 * §5.12 — was von der Anfrage NICHT im Angebot gelandet ist (`events.details.offerCoverage`).
 *
 * Die Positionsliste beantwortet „was ist drin?". Die Frage, an der ein Angebot scheitert, ist
 * aber „was fehlt?" — und die stand bisher nirgends. Wer dem Ergebnis vertraut, legt den
 * Anfragetext nicht daneben.
 *
 * Seit Punkt 77 (21.09.2026) gibt es dazu ZWEI Listen, weil es zwei Aussagen sind: was in KEINER
 * Fassung vorkommt (`unmatchedRequests`) und was nur in EINIGEN steht
 * (`teilweiseBeruecksichtigt`). Was ueberall vorkommt, erscheint in keiner von beiden.
 */
export interface KompositionsAbdeckung {
  /** Verstaendnistext des Komponisten (Schluessel `reasoning` im JSON). */
  reasoning: string;
  /** Verworfene Modell-Zeilen (erfundene Katalog-Ids). 0 ist der Normalfall. */
  strippedCount: number;
  /** Wuensche aus der Anfrage ohne Entsprechung im Katalog — benannt, nicht gezaehlt. */
  unmatchedRequests: string[];
  /**
   * Punkt 77, Zustand 2 (21.09.2026): Wuensche, die NUR IN EINIGEN Fassungen vorkommen — je
   * Wunsch die Namen dieser Fassungen. `unmatchedRequests` sagt „kommt nirgends vor"; hier steht
   * „kommt hier vor, dort nicht". Zwei verschiedene Aussagen, deshalb zwei Listen.
   */
  teilweiseBeruecksichtigt: { wunsch: string; inVarianten: string[] }[];
  /**
   * Punkt 77, das Wegklicken (21.09.2026): Punkte, die der Betrieb bewusst NICHT aufnimmt. Der
   * Wortlaut ist der Schluessel - die Listen oben entstehen bei jedem Kompositionslauf neu und
   * tragen keine stabilen Kennungen.
   *
   * Bewusst NICHT hier herausgefiltert: `leseKompositionsDetails` liest, was dasteht. Wer die
   * Listen anzeigt, filtert - so bleibt sichtbar, was weggeklickt wurde, und das Zurueckholen
   * hat eine Grundlage.
   */
  weggeklickt: string[];
}

export interface KompositionsDetails {
  antwortenAufFragen: KompositionsAntwort[];
  rueckfragen: string[];
  abdeckung: KompositionsAbdeckung;
}

function text(wert: unknown): string {
  return typeof wert === "string" ? wert.trim() : "";
}

const LEERE_ABDECKUNG: KompositionsAbdeckung = { reasoning: "", strippedCount: 0, unmatchedRequests: [], teilweiseBeruecksichtigt: [], weggeklickt: [] };

/** Liest die Komponisten-Felder aus `events.details`. Nie ein Wurf, nie ein geratener Inhalt. */
export function leseKompositionsDetails(details: unknown): KompositionsDetails {
  const leer: KompositionsDetails = { antwortenAufFragen: [], rueckfragen: [], abdeckung: { ...LEERE_ABDECKUNG } };
  if (!details || typeof details !== "object") return leer;
  const roh = details as Record<string, unknown>;

  const antworten: KompositionsAntwort[] = [];
  if (Array.isArray(roh.antwortenAufFragen)) {
    for (const eintrag of roh.antwortenAufFragen) {
      if (!eintrag || typeof eintrag !== "object") continue;
      const e = eintrag as Record<string, unknown>;
      const frage = text(e.frage);
      const antwort = text(e.antwort);
      // Eine Antwort ohne Frage (oder umgekehrt) ist keine beantwortete Frage.
      if (frage && antwort) antworten.push({ frage, antwort });
    }
  }

  const rueckfragen = Array.isArray(roh.rueckfragen) ? roh.rueckfragen.map(text).filter(Boolean) : [];

  // §5.12: Dieselbe Vorsicht wie oben — was nicht die erwartete Form hat, faellt weg. Eine
  // erfundene „offene Frage" waere schlimmer als gar keine Anzeige.
  const abdeckung: KompositionsAbdeckung = { ...LEERE_ABDECKUNG };
  if (roh.offerCoverage && typeof roh.offerCoverage === "object") {
    const c = roh.offerCoverage as Record<string, unknown>;
    abdeckung.reasoning = text(c.reasoning);
    abdeckung.strippedCount = typeof c.strippedCount === "number" && Number.isFinite(c.strippedCount) && c.strippedCount > 0 ? Math.floor(c.strippedCount) : 0;
    abdeckung.unmatchedRequests = Array.isArray(c.unmatchedRequests) ? c.unmatchedRequests.map(text).filter(Boolean) : [];
    // Punkt 77, Zustand 2: dieselbe Vorsicht. Ein Eintrag zaehlt nur, wenn BEIDES da ist — der
    // Wunsch und mindestens eine Fassung, in der er steht. „Kaffee kommt vor in: (nichts)" waere
    // keine Aussage, sondern eine Zeile, die der Betrieb nicht einordnen kann.
    abdeckung.teilweiseBeruecksichtigt = Array.isArray(c.teilweiseBeruecksichtigt)
      ? c.teilweiseBeruecksichtigt
          .map((eintrag) => {
            if (!eintrag || typeof eintrag !== "object") return null;
            const e = eintrag as Record<string, unknown>;
            const wunsch = text(e.wunsch);
            const inVarianten = Array.isArray(e.inVarianten) ? e.inVarianten.map(text).filter(Boolean) : [];
            return wunsch && inVarianten.length > 0 ? { wunsch, inVarianten } : null;
          })
          .filter((e): e is { wunsch: string; inVarianten: string[] } => e !== null)
      : [];
    abdeckung.weggeklickt = Array.isArray(c.weggeklickt) ? c.weggeklickt.map(text).filter(Boolean) : [];
  }

  return { antwortenAufFragen: antworten, rueckfragen, abdeckung };
}
